import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import UserApi from "../../../../api/users/users.js";
import OfficesApi from "../../../../api/offices/index.js";
import styled from "styled-components";
import AccessDeniedMessage from "../../../../components/AccessDeniedMessage.js";
import usePlatform from "../../../../hooks/usePlatform";

import { BreadcrumbPageHeader } from "@/components/BreadCrumbHeader";
import { usePermissions } from "@/hooks/usePermissions";
import UserDetailsBlock from "./UserDetailsBlock";
import UserRolesBlock from "./UserRolesBlock";
import ResetUserPasswordBlock from "./UserActionBlocks/ResetUserPasswordBlock";
import ResetUserMFABlock from "./UserActionBlocks/ResetUserMFABlock";
import ChangeUserPasswordBlock from "./UserActionBlocks/ChangeUserPassword";
import MFAToggleBlock from "./UserActionBlocks/MFAToggleBlock";
import UserActivationBlock from "./UserActionBlocks/UserActivationBlock";
import UserTooltipContent from "@/components/BreadCrumbHeader/Tooltips/UserTooltip";
import UserSignatureBlock from "./UserActionBlocks/UserSignatureBlock";
const UserDetails = styled(({ className }) => {
  const { currentUser, hasPermission, MONOLITH_PERMISSIONS, licenseInfo } =
    usePermissions();
  const { isOnPremises } = usePlatform();
  const { user_id } = useParams();

  const queryClient = useQueryClient();

  const hasAccessToProfile =
    currentUser.email === user_id ||
    hasPermission(MONOLITH_PERMISSIONS.USERS_MANAGE);

  const isReadOnly = licenseInfo?.is_expired ?? false;

  if (!hasAccessToProfile) return <AccessDeniedMessage />;

  const { data: userData } = useQuery({
    queryKey: ["users", "list", { email: user_id }],
    queryFn: () => UserApi.getUsers({ email: user_id, signature: true }),
    select: (data) => data[0],
    enabled: hasAccessToProfile,
  });

  const { data: officeData } = useQuery({
    queryKey: ["offices", "list"],
    queryFn: () => OfficesApi.getOffices(),
    enabled: hasAccessToProfile,
  });

  if (!userData || !officeData) return null;

  const handleRefetch = () => {
    queryClient.refetchQueries({
      queryKey: ["users", "list", { email: userData.email }],
    });
  };

  return (
    <div className={className}>
      <Helmet title={`User Details`} />
      <Helmet
        title={`User Details | ${userData.full_name} | ${userData.email}`}
      />
      <div className="header">
        <BreadcrumbPageHeader
          sectionName="Team Management"
          pageName={`${userData.full_name}`}
          TooltipContent={() => <UserTooltipContent defaultInfo={userData} />}
        />
        <SectionDivider />
      </div>
      <div className="content">
        {/* contain form width */}
        <div style={{ width: "600px" }}>
          <UserDetailsBlock
            userData={userData}
            offices={officeData}
            readOnly={isReadOnly}
            onSubmit={handleRefetch}
          />
          <SectionDivider />
          <UserRolesBlock
            userData={userData}
            readOnly={true}
            onSubmit={handleRefetch}
          />
          {(hasPermission(MONOLITH_PERMISSIONS.USERS_MANAGE) ||
            currentUser.user_id === userData.user_id) && (
            <>
              <SectionDivider />
              <UserSignatureBlock
                userData={userData}
                onSubmit={handleRefetch}
              />
            </>
          )}
          {/* CLOUD USER ACTIONS */}
          {!isOnPremises &&
            (hasPermission(MONOLITH_PERMISSIONS.USERS_MANAGE) ||
              currentUser.user_id === userData.user_id) && (
              <>
                <SectionDivider />
                <ResetUserPasswordBlock
                  userData={userData}
                  onSubmit={handleRefetch}
                />
                <SectionDivider />
                <ResetUserMFABlock
                  userData={userData}
                  onSubmit={handleRefetch}
                />
              </>
            )}
          {/* ON-PREM USER ACTIONS */}
          {isOnPremises &&
            (hasPermission(MONOLITH_PERMISSIONS.USERS_MANAGE) ||
              currentUser.user_id === userData.user_id) && (
              <>
                <SectionDivider />
                <ChangeUserPasswordBlock
                  userData={userData}
                  onSubmit={handleRefetch}
                />
                <SectionDivider />
                <MFAToggleBlock userData={userData} onSubmit={handleRefetch} />
              </>
            )}
          {/* ADMIN ONLY USER ACTIONS */}
          {hasPermission(MONOLITH_PERMISSIONS.USERS_MANAGE) && (
            <>
              <SectionDivider />
              <UserActivationBlock
                userData={userData}
                onSubmit={handleRefetch}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;

  .header {
    // flex: 0 0 auto;
    width: 100%;
  }

  .content {
    flex: 1;
    width: 100%;
    overflow-y: auto;
  }
`;

const SectionDivider = styled.div`
  border: 0.5px solid ${({ theme }) => theme.palette.divider};
  width: 100%;
`;

export default UserDetails;
